import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateCardRequestSchema, type CreateCardRequest } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/notify";
import { isApiClientError } from "@/lib/api-client";
import { useCreateCard } from "@/features/cards/hooks";

interface NewCardFormProps {
  deviceId: string;
  onDone: () => void;
}

export function NewCardForm({ deviceId, onDone }: NewCardFormProps) {
  const create = useCreateCard(deviceId);
  const form = useForm<CreateCardRequest>({
    resolver: zodResolver(CreateCardRequestSchema),
    defaultValues: { code: "", type: "DIGITAL", separator: undefined, name: undefined },
  });
  const type = form.watch("type");

  async function onSubmit(values: CreateCardRequest) {
    try {
      await create.mutateAsync(values);
      toast.success("Cartão criado.");
      onDone();
    } catch (err) {
      toast.error("Falha ao criar cartão.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  const errs = form.formState.errors;
  return (
    <form
      onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
      className="grid grid-cols-1 gap-3 rounded-md border border-border-subtle bg-bg-muted-rd p-3 sm:grid-cols-2"
    >
      <div className="space-y-1.5">
        <Label htmlFor="nc-code">Código</Label>
        <Input id="nc-code" {...form.register("code")} placeholder="EA03" />
        {errs.code && <p className="text-xs text-destructive">{errs.code.message}</p>}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="nc-type">Tipo</Label>
        <Select id="nc-type" {...form.register("type")}>
          <option value="DIGITAL">Digital</option>
          <option value="ANALOG">Analógico</option>
        </Select>
      </div>
      {type === "ANALOG" && (
        <div className="space-y-1.5">
          <Label htmlFor="nc-sep">Separador</Label>
          <Input id="nc-sep" {...form.register("separator")} placeholder="#" />
          {errs.separator && <p className="text-xs text-destructive">{errs.separator.message}</p>}
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="nc-name">Nome (opcional)</Label>
        <Input id="nc-name" {...form.register("name")} placeholder="Entradas analógicas 3" />
      </div>
      <div className="flex gap-2 sm:col-span-2">
        <Button type="submit" size="sm" disabled={create.isPending}>
          {create.isPending ? "Criando…" : "Criar cartão"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
